import React, { useState, useRef, useEffect, useCallback } from 'react';
import { imagenRegistroService, ImagenRegistro } from '../services/imagenRegistroService';

interface ImageUploadProps {
  registroLimpiezaId: number;
  onImageUploaded: (imagen: ImagenRegistro) => void;
  onError: (message: string) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ 
  registroLimpiezaId, 
  onImageUploaded, 
  onError 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Wrapper para el event listener del input de archivo
  const handleFileSelectWrapper = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  // Wrapper para el event listener del input de cámara
  const handleCameraCaptureWrapper = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      setIsCapturing(true);
      handleUpload(file);
    }
  };

  // Función para limpiar el input de archivo de forma segura
  const clearFileInput = useCallback(() => {
    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = '';
      } catch (error) {
        console.warn('No se pudo limpiar el input de archivo:', error);
        // Si hay un error, recreamos el elemento
        const newInput = fileInputRef.current.cloneNode(true) as HTMLInputElement;
        newInput.value = '';
        fileInputRef.current.parentNode?.replaceChild(newInput, fileInputRef.current);
        fileInputRef.current = newInput;
        newInput.addEventListener('change', handleFileSelectWrapper);
      }
    }
  }, []);

  // Función para limpiar el input de cámara de forma segura
  const clearCameraInput = useCallback(() => {
    if (cameraInputRef.current) {
      try {
        cameraInputRef.current.value = '';
      } catch (error) {
        console.warn('No se pudo limpiar el input de cámara:', error);
        // Si hay un error, recreamos el elemento
        const newInput = cameraInputRef.current.cloneNode(true) as HTMLInputElement;
        newInput.value = '';
        cameraInputRef.current.parentNode?.replaceChild(newInput, cameraInputRef.current);
        cameraInputRef.current = newInput;
        newInput.addEventListener('change', handleCameraCaptureWrapper);
      }
    }
  }, []);

  // Limpiar los inputs cuando el componente se desmonte
  useEffect(() => {
    return () => {
      clearFileInput();
      clearCameraInput();
    };
  }, [clearFileInput, clearCameraInput]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevenir interferencia de extensiones
    event.stopPropagation();
    
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleCameraCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevenir interferencia de extensiones
    event.stopPropagation();
    
    const file = event.target.files?.[0];
    if (file) {
      setIsCapturing(true);
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      onError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      onError('El archivo es demasiado grande. Máximo 5MB permitido');
      return;
    }

    setIsUploading(true);
    try {
      const imagen = await imagenRegistroService.uploadImage(
        registroLimpiezaId, 
        file, 
        descripcion || undefined
      );
      
      onImageUploaded(imagen);
      setDescripcion('');
      
      // Limpiar los inputs de forma segura
      clearFileInput();
      clearCameraInput();
      setIsCapturing(false);
    } catch (error) {
      console.error('Error al subir imagen:', error);
      onError('Error al subir la imagen. Intenta nuevamente.');
      setIsCapturing(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleUpload(files[0]);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      <h3 className="text-lg font-semibold mb-4">Subir Imagen</h3>
      
      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isUploading || isCapturing}
          data-testid="file-input"
          autoComplete="off"
        />
        
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCameraCapture}
          className="hidden"
          disabled={isUploading || isCapturing}
          data-testid="camera-input"
          autoComplete="off"
        />
        
        <div className="space-y-3">
          <div className="text-gray-600">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              disabled={isUploading || isCapturing}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {isUploading ? 'Subiendo...' : 'Seleccionar Archivo'}
            </button>
            
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                cameraInputRef.current?.click();
              }}
              disabled={isUploading || isCapturing}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {isCapturing ? 'Capturando...' : 'Tomar Foto'}
            </button>
          </div>
          
          <p className="text-sm text-gray-500">
            o arrastra y suelta una imagen aquí
          </p>
          
          <p className="text-xs text-gray-400">
            PNG, JPG, GIF hasta 5MB • Usa "Tomar Foto" para abrir la cámara
          </p>
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-2">
          Descripción (opcional)
        </label>
        <input
          type="text"
          id="descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Describe la imagen..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={isUploading || isCapturing}
        />
      </div>
    </div>
  );
};

export default ImageUpload;
